<script setup lang="ts" generic="T extends Model">
import { computed, type PropType, ref, watch } from 'vue';
import { Model } from '@/models/model';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { type TableColumnModel } from './interfaces/TableColumnModel';
import { type PaginatorData } from '../../common/interfaces/PaginatorData';

const props = defineProps({
  columns: {
    type: Array as PropType<TableColumnModel[]>,
    required: true
  },
  dataList: {
    type: Array as PropType<T[]>,
    required: true
  },
  expandedRows: {
    type: Array as PropType<T[]>
  },
  expandedRowIndex: {
    type: Number,
    default: -1
  },
  selectedRows: {
    type: Array as PropType<T[]>
  },
  loadingState: {
    type: Boolean
  },
  presort: {
    type: String
  },
  presortOrder: {
    type: Number,
    default: -1
  },
  selection: {
    type: Boolean
  },
  rows: {
    type: Number,
    default: 25
  },
  totalRecords: {
    type: Number
  },
  currentRow: {
    type: Number,
    default: 0
  }
});

const dataTableRef = ref(null);

const selectedRows = computed({
  get: () => props.selectedRows,
  set: value => {
    emit('update:selectedRows', value);
  }
});

watch(
  () => props.expandedRowIndex,
  (newId, oldId) => {
    if (oldId > -2 && newId !== oldId) {
      if (dataTableRef.value) {
        //@ts-expect-error not an error
        const oldRow = dataTableRef.value.$el.querySelector(`tr[data-p-index="${oldId}"]`);

        if (oldRow) {
          oldRow.classList.remove('expandedRow');
        }

        //@ts-expect-error not an error
        const row = dataTableRef.value.$el.querySelector(`tr[data-p-index="${newId}"]`);
        if (row) {
          row.classList.add('expandedRow');
        }
      }
    }
  }
);

const emit = defineEmits(['onRowExpand', 'onRowCollapse', 'onPerPageRowCountChanged', 'onRowClick', 'update:selectedRows', 'onPageChange',
  'onSortFieldChange', 'onSortOrderChange'
]);

const onRowExpand = (event: PointerEvent) => {
  //@ts-expect-error data key is not present on pointer event, it is being added dynamically.
  emit('onRowExpand', event.data);
};

const onRowCollapse = (event: PointerEvent) => {
  //@ts-expect-error data key is not present on pointer event, it is being added dynamically.
  emit('onRowCollapse', event.data);
};

const onPerPageRowCountChanged = (data: number) => {
  emit('onPerPageRowCountChanged', data);
};

const onRowClick = (event: PointerEvent) => {
  //@ts-expect-error data key is not present on pointer event, it is being added dynamically.
  emit('onRowClick', event.data);
};

const onPageChange = (event: PaginatorData) => {
  emit('onPageChange', event);
};

const onSortFieldChange = (sort: string) => {
  emit('onSortFieldChange', sort);
};

const onSortOrderChange = (order: number | undefined) => {
  emit('onSortOrderChange', order);
};
</script>

<template>
  <div class="table-root">
    <DataTable :value="dataList"
      ref="dataTableRef"
      removableSort
      :sortField="presort"
      :sortOrder="presortOrder"
      scrollable
      scrollHeight="flex"
      paginator
      :first="currentRow"
      :row-hover="true"
      :expandedRows="expandedRows"
      v-model:selection="selectedRows"
      @rowExpand="onRowExpand"
      @rowCollapse="onRowCollapse"
      :selectAll="false"
      :loading="loadingState"
      :rows="rows"
      @rowClick="onRowClick"
      :rowsPerPageOptions="[10, 25, 50]"
      :totalRecords="totalRecords"
      :lazy="totalRecords !== undefined"
      @page="onPageChange"
      @update:rows="onPerPageRowCountChanged"
      @update:sortField="onSortFieldChange"
      @update:sortOrder="onSortOrderChange"
      paginatorTemplate="RowsPerPageDropdown PrevPageLink CurrentPageReport NextPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}">
      <Column selectionMode="multiple"
        headerStyle="width: 3rem"
        v-if="selection"
        frozen/>
      <Column v-for="col, index of columns"
        :key="col.id"
        :field="col.key"
        :header="col.header"
        :frozen="col.frozen"
        :sortable="col.sortable">
          <template #body="{data}">
            <slot name="bodyContent"
              :data="data"
              :col="col"
              :index="index">
            </slot>
          </template>
        </Column>
        <Column expander style="width: 5rem" v-if="expandedRows"/>
        <template #expansion="{data}">
          <slot name="expandContent" :data="data"></slot>
        </template>
        <template #empty>
          <slot name="emptyContent"></slot>
        </template>
    </DataTable>
  </div>
</template>

<style scoped lang="scss">
.table-root {
  height: 100%;
}
:deep(.p-datatable-thead > tr > th) {
  background-color: hsl(216, 33%, 97%);
}
:deep(.p-sortable-column) {
  cursor: pointer;
  &:hover {
    background-color: hsl(216, 100%, 85%, 0.5);
  }
}
:deep(.p-column-title) {
  margin-right: 8px;
}
:deep(.p-datatable-paginator-bottom) {
  border-top: 1px solid hsl(214, 70%, 96%);
}
:deep(.p-datatable-row-expansion) {
  background: #f8f8f8;
}
:deep(.expandedRow) {
  background: #f2f2f2;
}
// disabling the header checkbox, as checking it select all rows instead of only the current page,
// customizing the function isn't worth at the time, so instead just hiding the header checkbox
:deep(thead > tr) {
  .p-checkbox {
    display: none;
  }
}
</style>
